/**
 * useFolderShare Hook
 *
 * E035.4: Hook for folder share operations.
 * Provides methods and state for folder permission management.
 *
 * Architecture: Folder structure lives in Y.js (local-first),
 * permissions live in PostgreSQL (server-authoritative).
 * This hook gets folder name from Y.js, permissions from backend.
 */

import { AuthService } from '@affine/core/modules/cloud';
import type {
  FolderGroupRole,
  FolderOwner,
  FolderPermissions,
  FolderRole,
  GrantedFolderUser,
  GroupData,
} from '@affine/core/modules/folder-api';
import {
  FolderApiService,
  FolderMutationOutboxService,
  NO_FOLDER_PERMISSIONS,
  OWNER_FOLDER_PERMISSIONS,
} from '@affine/core/modules/folder-api';
import {
  AccessibleFolderCacheStore,
  OrganizeService,
} from '@affine/core/modules/organize';
import { ObjectPermissionMutationService } from '@affine/core/modules/permissions';
import {
  recordObjectConfigurationTraceEvent,
  traceObjectConfigurationRequest,
} from '@affine/core/modules/permissions/utils/object-configuration-trace';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { FolderRole as FolderRoleEnum, Permission } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { LiveData, useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const ALL_MEMBERS_GROUP_NAME = 'all-members';

/** Group role with resolved group name for display. */
export interface FolderGroupRoleWithName extends FolderGroupRole {
  groupName: string;
}

export interface FolderGrantUser extends FolderOwner {
  workspaceRole: Permission;
}

export interface UseFolderShareResult {
  /** Folder name */
  folderName: string | null;
  /** Folder owner */
  owner: FolderOwner | null;
  /** Current user's permissions for this folder */
  permissions: FolderPermissions | null;
  /** Users with roles on this folder */
  grantedUsers: GrantedFolderUser[];
  /** Total count of users */
  totalUsers: number;
  /** Folder visibility (private/public) */
  visibility: string;
  /** Whether folder inherits permissions from parent */
  inheritPermissions: boolean;
  isUpdatingInheritance: boolean;
  /** Whether the folder has a structural or virtual permission parent */
  hasParent: boolean;
  /** Groups with roles on this folder (with resolved names) */
  folderGroupRoles: FolderGroupRoleWithName[];
  /** All workspace groups (for adding) */
  workspaceGroups: GroupData[];
  /** Loading state */
  isLoading: boolean;
  /** Whether the current permission snapshot came from the server. */
  isServerConfirmed: boolean;
  /** Error message */
  error: string | null;
  /** Grant a role to a user */
  grantRole: (
    userId: string,
    role: FolderRole,
    user?: FolderGrantUser
  ) => Promise<boolean>;
  /** Revoke a user's role */
  revokeRole: (userId: string) => Promise<boolean>;
  /** Update folder visibility */
  setVisibility: (visibility: string) => Promise<void>;
  /** Update folder inheritPermissions */
  setInheritPermissions: (inherit: boolean) => Promise<void>;
  /** Rename folder */
  renameFolder: (name: string) => Promise<boolean>;
  /** Grant a group role on this folder */
  grantGroupRole: (groupId: string, role: FolderRole) => Promise<boolean>;
  /** Revoke a group role from this folder */
  revokeGroupRole: (groupId: string) => Promise<boolean>;
  /** Refresh the data */
  refresh: () => Promise<void>;
}

export function useFolderShare(folderId: string): UseFolderShareResult {
  const t = useI18n();
  const workspaceService = useService(WorkspaceService);
  const folderApiService = useService(FolderApiService);
  const folderMutationOutbox = useService(FolderMutationOutboxService);
  const objectPermissionMutation = useService(ObjectPermissionMutationService);
  const organizeService = useService(OrganizeService);
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const authService = useService(AuthService);

  const workspaceId = workspaceService.workspace.id;
  const account = useLiveData(authService.session.account$);
  const accessibleFolders = useLiveData(
    useMemo(
      () =>
        LiveData.from(accessibleFolderCache.watchAllAccessibleFolders(), []),
      [accessibleFolderCache]
    )
  );
  const cachedFolder = useMemo(
    () => accessibleFolders.find(folder => folder.id === folderId) ?? null,
    [accessibleFolders, folderId]
  );
  const cachedFolderName = cachedFolder?.name ?? null;
  const cachedFolderOwnerUserId = cachedFolder?.ownerUserId ?? null;
  const cachedFolderVisibility = cachedFolder?.visibility ?? null;
  const cachedFolderInheritPermissions =
    cachedFolder?.inheritPermissions ?? true;
  const cachedFolderParentId = cachedFolder?.parentId ?? null;
  const cachedFolderIsWorkspaceRoot = cachedFolder?.isWorkspaceRoot ?? false;
  const hasCachedFolder = cachedFolder !== null;
  const accountId = account?.id ?? null;
  const accountName = account?.label ?? account?.email ?? account?.id ?? null;
  const accountEmail = account?.email ?? '';
  const accountAvatar = account?.avatar ?? null;
  const currentUserOwnsCachedFolder =
    !!accountId && cachedFolderOwnerUserId === accountId;

  // Get folder name from Y.js (local-first source of truth for structure)
  const folderNode$ = organizeService.folderTree.folderNode$(folderId);
  const folderNode = useLiveData(folderNode$);
  const folderNameFromYjs = useLiveData(folderNode?.name$ ?? null);

  const [owner, setOwner] = useState<FolderOwner | null>(null);
  const [permissions, setPermissions] = useState<FolderPermissions | null>(
    null
  );
  const [grantedUsers, setGrantedUsers] = useState<GrantedFolderUser[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [visibility, setVisibilityState] = useState('private');
  const [inheritPermissions, setInheritState] = useState(true);
  const [isUpdatingInheritance, setIsUpdatingInheritance] = useState(false);
  const [hasParent, setHasParent] = useState(false);
  const [folderGroupRoles, setFolderGroupRoles] = useState<
    FolderGroupRoleWithName[]
  >([]);
  const [workspaceGroups, setWorkspaceGroups] = useState<GroupData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isServerConfirmed, setIsServerConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestSeqRef = useRef(0);
  const activeFetchRef = useRef<Promise<void> | null>(null);
  const activeFolderIdRef = useRef(folderId);
  const hasLoadedRef = useRef(false);
  const workspaceGroupsRefreshRequestedRef = useRef(false);

  const fetchDataRequest = useCallback(async () => {
    const requestSeq = ++requestSeqRef.current;
    const isCurrentRequest = () => requestSeqRef.current === requestSeq;
    const markLoaded = () => {
      if (!isCurrentRequest()) {
        return false;
      }
      hasLoadedRef.current = true;
      setIsLoading(false);
      return true;
    };
    const refreshWorkspaceGroups = (fallbackGroups: GroupData[]) => {
      if (workspaceGroupsRefreshRequestedRef.current) {
        return;
      }
      workspaceGroupsRefreshRequestedRef.current = true;
      void traceObjectConfigurationRequest('workspace-groups', () =>
        folderApiService.getGroups(workspaceId)
      )
        .then(groups => {
          setWorkspaceGroups(groups);
        })
        .catch(() => {
          workspaceGroupsRefreshRequestedRef.current = false;
          if (isCurrentRequest()) {
            setWorkspaceGroups(fallbackGroups);
          }
        });
    };

    if (!hasLoadedRef.current) {
      setIsLoading(true);
    }
    setIsServerConfirmed(false);
    setError(null);

    const applyCachedOwnerFallback = () => {
      if (
        !hasCachedFolder ||
        !currentUserOwnsCachedFolder ||
        cachedFolderVisibility === null
      ) {
        return false;
      }

      if (!isCurrentRequest()) {
        return true;
      }

      setPermissions(OWNER_FOLDER_PERMISSIONS);
      setOwner(
        accountId
          ? {
              id: accountId,
              name: accountName ?? accountId,
              email: accountEmail,
              avatarUrl: accountAvatar,
            }
          : null
      );
      setVisibilityState(cachedFolderVisibility);
      setInheritState(cachedFolderInheritPermissions);
      setHasParent(
        cachedFolderParentId !== null || !cachedFolderIsWorkspaceRoot
      );
      setGrantedUsers([]);
      setTotalUsers(0);
      const cachedGroups = folderApiService.getCachedGroups(workspaceId);
      recordObjectConfigurationTraceEvent('cache-read', {
        cache: 'workspace-groups',
        hit: cachedGroups.length > 0,
      });
      const allMembersGroup = cachedGroups.find(
        group => group.isAutoSync || group.name === ALL_MEMBERS_GROUP_NAME
      );
      setFolderGroupRoles(
        cachedFolderVisibility === 'public' &&
          cachedFolderInheritPermissions &&
          allMembersGroup
          ? [
              {
                id: `workspace:${allMembersGroup.id}`,
                groupId: allMembersGroup.id,
                role: FolderRoleEnum.Admin,
                groupName: allMembersGroup.name,
                sourceType: 'workspace',
                sourceName: null,
                isDirectOverride: false,
                isInherited: true,
                isInheritanceBlocked: false,
              },
            ]
          : []
      );
      setWorkspaceGroups(cachedGroups);
      markLoaded();
      refreshWorkspaceGroups(cachedGroups);
      return true;
    };

    const hasCachedOwnerFallback = applyCachedOwnerFallback();
    recordObjectConfigurationTraceEvent('cache-read', {
      cache: 'folder-owner',
      hit: hasCachedOwnerFallback,
    });

    try {
      const folder = await traceObjectConfigurationRequest(
        'effective-access',
        () => folderApiService.getFolder(workspaceId, folderId)
      );

      if (!isCurrentRequest()) {
        return;
      }

      if (folder) {
        recordObjectConfigurationTraceEvent('effective-access-loaded', {
          entries: folder.effectiveAccess.length,
          permissionGraphVersions: new Set(
            folder.effectiveAccess
              .map(entry => entry.permissionGraphVersion)
              .filter(Boolean)
          ).size,
        });
        setPermissions(folder.permissions);
        setOwner(folder.owner);
        setVisibilityState(folder.visibility);
        setInheritState(folder.inheritPermissions);
        // Ordinary top-level folders inherit from the workspace's virtual
        // Public root even though their structural parentId is null. Only the
        // actual workspace root has no permission parent.
        setHasParent(folder.parentId !== null || !folder.isWorkspaceRoot);

        if (folder.permissions.Folder_Users_Read) {
          const userAccess: GrantedFolderUser[] = folder.effectiveAccess
            .filter(entry => entry.principalType === 'user')
            .map(entry => ({
              role: entry.effectiveRole,
              workspaceRole: entry.workspaceRole ?? Permission.Reader,
              sourceType: entry.sourceType,
              sourceName: entry.sourceName,
              isDirectOverride: entry.isDirectOverride,
              isInherited: entry.isInherited,
              isInheritanceBlocked: entry.isInheritanceBlocked,
              isAreaBlocked: entry.isAreaBlocked,
              areaBoundaryKind: entry.areaBoundaryKind,
              areaBoundaryRootFolderId: entry.areaBoundaryRootFolderId,
              areaBoundaryRootName: entry.areaBoundaryRootName,
              permissionGraphVersion: entry.permissionGraphVersion,
              user: {
                id: entry.principalId,
                name: entry.displayName,
                email: entry.email ?? '',
                avatarUrl: entry.avatarUrl,
              },
            }));
          setGrantedUsers(userAccess);
          setTotalUsers(userAccess.length);

          setFolderGroupRoles(
            folder.effectiveAccess
              .filter(entry => entry.principalType === 'group')
              .map(entry => ({
                id: `${entry.sourceId ?? 'direct'}:${entry.principalId}`,
                groupId: entry.principalId,
                role: entry.effectiveRole,
                groupName: entry.displayName,
                sourceType: entry.sourceType,
                sourceName: entry.sourceName,
                isDirectOverride: entry.isDirectOverride,
                isInherited: entry.isInherited,
                isInheritanceBlocked: entry.isInheritanceBlocked,
                isAreaBlocked: entry.isAreaBlocked,
                areaBoundaryKind: entry.areaBoundaryKind,
                areaBoundaryRootFolderId: entry.areaBoundaryRootFolderId,
                areaBoundaryRootName: entry.areaBoundaryRootName,
                permissionGraphVersion: entry.permissionGraphVersion,
              }))
          );
          const cachedGroups = folderApiService.getCachedGroups(workspaceId);
          setWorkspaceGroups(cachedGroups);
          refreshWorkspaceGroups(cachedGroups);
        } else {
          setGrantedUsers([]);
          setTotalUsers(0);
          setFolderGroupRoles([]);
          setWorkspaceGroups(folderApiService.getCachedGroups(workspaceId));
        }
      } else {
        const appliedFallback =
          hasCachedOwnerFallback || applyCachedOwnerFallback();
        if (!appliedFallback) {
          setPermissions(
            workspaceService.workspace.flavour === 'local'
              ? OWNER_FOLDER_PERMISSIONS
              : NO_FOLDER_PERMISSIONS
          );
        }
        setGrantedUsers([]);
        setTotalUsers(0);
      }
      if (isCurrentRequest()) {
        setIsServerConfirmed(true);
      }
    } catch (err) {
      if (!isCurrentRequest()) {
        return;
      }

      console.error('Failed to fetch folder permissions:', err);
      const appliedFallback =
        hasCachedOwnerFallback || applyCachedOwnerFallback();
      if (!appliedFallback) {
        setPermissions(
          workspaceService.workspace.flavour === 'local'
            ? OWNER_FOLDER_PERMISSIONS
            : NO_FOLDER_PERMISSIONS
        );
        setGrantedUsers([]);
        setTotalUsers(0);
        setFolderGroupRoles([]);
        setWorkspaceGroups([]);
      }
    } finally {
      markLoaded();
    }
  }, [
    accountAvatar,
    accountEmail,
    accountId,
    accountName,
    cachedFolderInheritPermissions,
    cachedFolderIsWorkspaceRoot,
    cachedFolderParentId,
    cachedFolderVisibility,
    currentUserOwnsCachedFolder,
    folderApiService,
    workspaceId,
    folderId,
    hasCachedFolder,
    workspaceService.workspace.flavour,
  ]);

  const fetchData = useCallback(() => {
    if (activeFetchRef.current) {
      recordObjectConfigurationTraceEvent('request-deduplicated', {
        request: 'effective-access',
      });
      return activeFetchRef.current;
    }
    const request = fetchDataRequest();
    activeFetchRef.current = request;
    void request.then(
      () => {
        if (activeFetchRef.current === request) activeFetchRef.current = null;
      },
      () => {
        if (activeFetchRef.current === request) activeFetchRef.current = null;
      }
    );
    return request;
  }, [fetchDataRequest]);

  const getMutationErrorMessage = useCallback(
    (_err: unknown, fallbackKey: keyof typeof t) => t[fallbackKey](),
    [t]
  );

  useEffect(() => {
    if (activeFolderIdRef.current === folderId) return;
    activeFolderIdRef.current = folderId;
    activeFetchRef.current = null;
    hasLoadedRef.current = false;
    requestSeqRef.current += 1;
    setIsServerConfirmed(false);
  }, [folderId]);

  useEffect(() => {
    fetchData().catch(console.error);
  }, [fetchData]);

  const grantRole = useCallback(
    async (
      userId: string,
      role: FolderRole,
      user?: FolderGrantUser
    ): Promise<boolean> => {
      try {
        await objectPermissionMutation.execute({
          action: 'grant',
          principal: { id: userId, kind: 'user' },
          role,
          target: { id: folderId, kind: 'folder' },
        });
        const isNewGrant =
          user !== undefined &&
          !grantedUsers.some(granted => granted.user.id === userId);
        setGrantedUsers(users => {
          const existing = users.find(granted => granted.user.id === userId);
          if (!existing && user) {
            return [
              ...users,
              {
                role,
                workspaceRole: user.workspaceRole,
                sourceType: 'direct',
                sourceName: null,
                isDirectOverride: true,
                isInherited: false,
                isInheritanceBlocked: role === FolderRoleEnum.None,
                isAreaBlocked: false,
                areaBoundaryKind: null,
                areaBoundaryRootFolderId: null,
                areaBoundaryRootName: null,
                user: {
                  id: user.id,
                  name: user.name,
                  email: user.email,
                  avatarUrl: user.avatarUrl,
                },
              },
            ];
          }
          return users.map(granted =>
            granted.user.id === userId
              ? {
                  ...granted,
                  role,
                  sourceType: 'direct',
                  sourceName: null,
                  isDirectOverride: true,
                  isInherited: false,
                  isInheritanceBlocked: role === FolderRoleEnum.None,
                }
              : granted
          );
        });
        if (isNewGrant) {
          setTotalUsers(total => total + 1);
        }
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to grant role');
        return false;
      }
    },
    [folderId, grantedUsers, objectPermissionMutation]
  );

  const revokeRole = useCallback(
    async (userId: string): Promise<boolean> => {
      try {
        await objectPermissionMutation.execute({
          action: 'revoke',
          principal: { id: userId, kind: 'user' },
          target: { id: folderId, kind: 'folder' },
        });
        setGrantedUsers(users =>
          users.filter(user => user.user.id !== userId || user.isInherited)
        );
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to revoke role');
        return false;
      }
    },
    [folderId, objectPermissionMutation]
  );

  const setVisibility = useCallback(
    async (newVisibility: string) => {
      const previousVisibility = visibility;
      try {
        setVisibilityState(newVisibility);
        await folderMutationOutbox.enqueueUpdateFolderSettings(folderId, {
          visibility: newVisibility as 'private' | 'public',
        });
      } catch (err) {
        setVisibilityState(previousVisibility);
        setError(
          getMutationErrorMessage(
            err,
            'com.streamlain.folder-share.error.visibility-update-failed'
          )
        );
      }
    },
    [folderId, folderMutationOutbox, getMutationErrorMessage, visibility]
  );

  const setInheritPermissions = useCallback(
    async (inherit: boolean) => {
      const previousInheritPermissions = inheritPermissions;
      const previousGrantedUsers = grantedUsers;
      const previousFolderGroupRoles = folderGroupRoles;
      try {
        setIsUpdatingInheritance(true);
        setInheritState(inherit);
        if (!inherit) {
          setGrantedUsers(users => users.filter(user => !user.isInherited));
          setFolderGroupRoles(groups =>
            groups.filter(group => !group.isInherited)
          );
        }
        await folderMutationOutbox.enqueueUpdateFolderSettings(folderId, {
          inheritPermissions: inherit,
        });
      } catch (err) {
        setInheritState(previousInheritPermissions);
        setGrantedUsers(previousGrantedUsers);
        setFolderGroupRoles(previousFolderGroupRoles);
        setError(
          getMutationErrorMessage(
            err,
            'com.streamlain.folder-share.error.inheritance-update-failed'
          )
        );
      } finally {
        setIsUpdatingInheritance(false);
      }
    },
    [
      folderId,
      folderMutationOutbox,
      folderGroupRoles,
      getMutationErrorMessage,
      grantedUsers,
      inheritPermissions,
    ]
  );

  const renameFolder = useCallback(
    async (name: string): Promise<boolean> => {
      const nextName = name.trim();
      const previousName = folderNameFromYjs ?? cachedFolderName ?? '';
      if (!nextName || nextName === previousName) {
        return true;
      }

      const optimisticSnapshot =
        accessibleFolderCache.captureOptimisticSnapshot();
      try {
        if (workspaceService.workspace.flavour === 'local') {
          folderNode?.rename(nextName);
        } else {
          accessibleFolderCache.optimisticRenameFolder(folderId, nextName);
          await folderMutationOutbox.enqueueRenameFolder(folderId, nextName);
        }
        return true;
      } catch (err) {
        accessibleFolderCache.restoreOptimisticSnapshot(optimisticSnapshot);
        setError(
          err instanceof Error ? err.message : 'Failed to rename folder'
        );
        return false;
      }
    },
    [
      accessibleFolderCache,
      cachedFolderName,
      folderId,
      folderMutationOutbox,
      folderNameFromYjs,
      folderNode,
      workspaceService.workspace.flavour,
    ]
  );

  const grantGroupRole = useCallback(
    async (groupId: string, role: FolderRole): Promise<boolean> => {
      try {
        await objectPermissionMutation.execute({
          action: 'grant',
          principal: { id: groupId, kind: 'group' },
          role,
          target: { id: folderId, kind: 'folder' },
        });
        const group = workspaceGroups.find(group => group.id === groupId);
        setFolderGroupRoles(groups => [
          ...groups.filter(groupRole => groupRole.groupId !== groupId),
          {
            id: `direct:${groupId}`,
            groupId,
            role,
            groupName: group?.name ?? groupId,
            sourceType: 'direct',
            sourceName: null,
            isDirectOverride: true,
            isInherited: false,
            isInheritanceBlocked: role === FolderRoleEnum.None,
            isAreaBlocked: false,
            areaBoundaryKind: null,
            areaBoundaryRootFolderId: null,
            areaBoundaryRootName: null,
          },
        ]);
        fetchData().catch(console.error);
        return true;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to grant group role'
        );
        return false;
      }
    },
    [fetchData, folderId, objectPermissionMutation, workspaceGroups]
  );

  const revokeGroupRole = useCallback(
    async (groupId: string): Promise<boolean> => {
      try {
        await objectPermissionMutation.execute({
          action: 'revoke',
          principal: { id: groupId, kind: 'group' },
          target: { id: folderId, kind: 'folder' },
        });
        setFolderGroupRoles(groups =>
          groups.filter(group => group.groupId !== groupId || group.isInherited)
        );
        return true;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to revoke group role'
        );
        return false;
      }
    },
    [folderId, objectPermissionMutation]
  );

  return {
    folderName: folderNameFromYjs ?? cachedFolderName,
    owner,
    permissions,
    grantedUsers,
    totalUsers,
    visibility,
    inheritPermissions,
    isUpdatingInheritance,
    hasParent,
    folderGroupRoles,
    workspaceGroups,
    isLoading,
    isServerConfirmed,
    error,
    grantRole,
    revokeRole,
    setVisibility,
    setInheritPermissions,
    renameFolder,
    grantGroupRole,
    revokeGroupRole,
    refresh: fetchData,
  };
}
